import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Card, Text, tokens, Stack } from '@hotjar/hotsauce';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useHelpHotjarUrl } from '@hotjar/translations';
import { useGtmOAuthUrl } from '../../hooks/useGtmOAuthUrl';
import { CMS } from './types';
import type { IntegrationsIconProps } from './IntegrationsIcon';
import { useIntegrationPlatforms } from './hooks/useIntegrationPlatforms';
import { IntegrationsIcon } from './IntegrationsIcon';
import type { SetupWizardData } from '../../hooks/useSetupWizard';
import { STEPS } from '../types';

const StyledCard = styled(Card)`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  background-color: ${tokens.color.ui_background};
  border-radius: ${tokens.radius.l};
  box-shadow: unset;
`;

const StyledIntegrationsIcon = styled(IntegrationsIcon)<{ inSetupPage?: boolean }>`
  ${({ inSetupPage }) =>
    inSetupPage &&
    `
      ${StyledCard}:nth-child(-n + 4) & {
        background-size: ${tokens.spacer.x5};
        min-height: ${tokens.spacer.x5};
      }
    `}
`;

const StyledCardBody = styled(Card.Body)`
  padding: ${tokens.spacer.x3} 0;
`;

const Wrapper = styled.div<{ cmsGrid?: boolean }>`
  display: grid;
  grid-gap: ${tokens.spacer.x2};
  grid-template-columns: 1fr 1fr 1fr 1fr;
`;

const StyledButton = styled.button`
  width: 100%;
  height: 100%;
  padding: 0 ${tokens.spacer.x2pt5};
  background-color: ${tokens.color.ui_background};
  border: 1px solid ${tokens.color.ui_border_light};
  border-radius: ${tokens.radius.l};

  :focus {
    border: 1px solid ${tokens.color.primary_cta};
  }
`;

const StyledLink = styled.a`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  width: 100%;
  height: 100%;
  padding: 0 ${tokens.spacer.x2pt5};
  background-color: ${tokens.color.ui_background};
  border: 1px solid ${tokens.color.ui_border_light};
  border-radius: ${tokens.radius.l};
  cursor: pointer;

  :hover,
  :focus {
    text-decoration: none;
  }

  :focus {
    border: 1px solid ${tokens.color.primary_cta};
  }
`;

interface IntegrationGuidesProps {
  exclude?: CMS | 'gtm';
  goToStep: SetupWizardData['goToStep'];
  onClick: (cms: CMS) => void;
  inSetupPage?: boolean;
}

export const IntegrationGuides = ({
  exclude,
  goToStep,
  onClick,
  inSetupPage,
}: IntegrationGuidesProps): JSX.Element => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const { tagRecording, click: trackClick } = useTrack();
  const platforms = useIntegrationPlatforms(exclude);
  const gtmOAuthURL = useGtmOAuthUrl();

  const handleOnClick = (cms: CMS | 'More' | 'gtm') => {
    tagRecording(`Dashboard TC Install: Clicked CMS - ${cms}`);
    trackClick(cms, 'Integrate Hotjar Dashboard', {});

    if (cms === 'gtm') {
      window.location.assign(gtmOAuthURL);
    } else {
      if (inSetupPage) {
        goToStep(STEPS.PLATFORMS);
      }

      if (cms !== 'More') {
        onClick(cms);
      }
    }
  };

  return (
    <Stack
      style={{ paddingTop: inSetupPage ? tokens.spacer.x6 : tokens.spacer.x1 }}
      direction="column"
      spacing={inSetupPage ? 'x1pt5' : 'x4'}
    >
      {inSetupPage ? (
        <Text as="h3" textType="header5">
          {t('activation:setup-wizard.integrations.title-standalone')}
        </Text>
      ) : (
        <Text textType="body1" textColor="text_light">
          {t('activation:setup-wizard.integrations.title')}
        </Text>
      )}
      <Wrapper>
        {platforms.map((platform) => {
          if (platform.id === CMS.GTM && !gtmOAuthURL) {
            return null;
          }

          if (!inSetupPage) {
            return (
              <StyledCard key={platform.id}>
                <StyledButton type="button" onClick={() => handleOnClick(platform.id as CMS)}>
                  <StyledCardBody>
                    <Stack direction="column">
                      <StyledIntegrationsIcon
                        inSetupPage={inSetupPage}
                        icon={platform.id as IntegrationsIconProps['icon']}
                      />
                      <Text textType="subtitle2" style={{ textAlign: 'center' }}>
                        {platform.title}
                      </Text>
                    </Stack>
                  </StyledCardBody>
                </StyledButton>
              </StyledCard>
            );
          }

          if ([CMS.GTM, CMS.WORDPRESS, CMS.SHOPIFY].includes(platform.id)) {
            return (
              <StyledCard key={platform.id}>
                <StyledButton
                  type="button"
                  onClick={() => handleOnClick(platform.id as CMS)}
                  data-testid="platform-card"
                >
                  <StyledCardBody>
                    <Stack direction="column">
                      <StyledIntegrationsIcon
                        inSetupPage={inSetupPage}
                        icon={platform.id as IntegrationsIconProps['icon']}
                      />
                      <Text textType="subtitle2" style={{ textAlign: 'center' }}>
                        {platform.title}
                      </Text>
                    </Stack>
                  </StyledCardBody>
                </StyledButton>
              </StyledCard>
            );
          }

          return null;
        })}
        <StyledCard>
          {inSetupPage ? (
            <StyledLink as="button" onClick={() => handleOnClick('More')} role="button">
              <StyledCardBody>
                <Stack direction="column">
                  <Text textType="subtitle2" style={{ textAlign: 'center' }}>
                    {t('activation:setup-wizard.integrations.see-more')}
                  </Text>
                </Stack>
              </StyledCardBody>
            </StyledLink>
          ) : (
            <StyledLink
              onClick={() => handleOnClick('More')}
              href={`${helpHotjarUrl}/articles/115009336727-How-to-Install-your-Hotjar-Tracking-Code`}
              target="_blank"
              rel="noreferrer"
            >
              <StyledCardBody>
                <Stack direction="column">
                  <Text textType="subtitle2" style={{ textAlign: 'center' }}>
                    {t('activation:setup-wizard.integrations.see-more')}
                  </Text>
                </Stack>
              </StyledCardBody>
            </StyledLink>
          )}
        </StyledCard>
      </Wrapper>
    </Stack>
  );
};
